import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import introImg from '../Images/Intro.png';

const FEATURES = [
  { icon: '🫁', title: 'Biometric Attendance', desc: 'Real-time eSSL/ZKTeco device integration with shift-aware auto-processing', color: '#06b6d4', tag: 'LIVE' },
  { icon: '🏥', title: 'OPD Queue System', desc: 'Smart token generation, wait-time estimation, doctor queue management', color: '#10b981', tag: 'SMART' },
  { icon: '📝', title: 'Digital Evaluation', desc: 'Red-pen PDF annotation, section marks, video audit recording', color: '#8b5cf6', tag: 'COE' },
  { icon: '🎓', title: 'Admission Portal', desc: 'NEET-based applications, document uploads, auto merit listing', color: '#6366f1', tag: 'PUBLIC' },
  { icon: '💊', title: 'Clinical Records', desc: 'Vitals, ICD-10 diagnoses, structured prescriptions, follow-ups', color: '#f59e0b', tag: 'CLINICAL' },
  { icon: '🔬', title: 'Laboratory', desc: 'Sample collection workflows, test orders, results, billing', color: '#ec4899', tag: 'LAB' },
  { icon: '💰', title: 'Finance & Payroll', desc: 'Course fees, salary structures, payment tracking, expense ledger', color: '#06b6d4', tag: 'ERP' },
  { icon: '📋', title: 'Thesis Pipeline', desc: 'Proposal → Faculty Review → COE Evaluation → Marks Publication', color: '#8b5cf6', tag: 'ACADEMIC' },
  { icon: '⚖️', title: 'Grievance Portal', desc: 'Ticket management, escalation, department resolution tracking', color: '#10b981', tag: 'HR' },
  { icon: '👨‍👩‍👧', title: 'Parent Portal', desc: 'Real-time child attendance, fees, schedule, announcements', color: '#ec4899', tag: 'FAMILY' },
  { icon: '🛡️', title: 'Role-Based Security', desc: '18+ roles, JWT auth, granular permissions, full audit trails', color: '#6366f1', tag: 'SECURE' },
  { icon: '📊', title: 'Analytics', desc: 'Multi-role dashboards, attendance trends, financial KPIs', color: '#f59e0b', tag: 'INSIGHTS' },
  { icon: '🏠', title: 'Hostel', desc: 'Room allocation, facilities management, resident tracking', color: '#06b6d4', tag: 'FACILITY' },
  { icon: '📚', title: 'Library', desc: 'Book catalog, digital resources, issue & return tracking', color: '#10b981', tag: 'RESOURCE' },
  { icon: '🚌', title: 'Transport', desc: 'Route planning, vehicle scheduling, transport allocation', color: '#8b5cf6', tag: 'LOGISTICS' },
  { icon: '✍️', title: 'Paper Evaluator', desc: 'Secure bundle grading, canvas annotation, video audit trail', color: '#ec4899', tag: 'EXAM' },
  { icon: '👨‍🏫', title: 'HOD Dashboard', desc: 'Dept management, class scheduling, faculty & student assignments', color: '#6366f1', tag: 'ADMIN' },
  { icon: '🩺', title: 'Doctor Dashboard', desc: 'Live OPD queue, patient records, prescriptions, consultation notes', color: '#f59e0b', tag: 'OPD' },
];

const ROLES = [
  { title: 'Administrator', icon: '⚙️', color: '#6366f1', desc: 'Full system control, user management, biometric sync, policy configuration and audit', metrics: ['All Modules', 'System Config', 'User CRUD', 'Audit Logs'] },
  { title: 'Principal', icon: '🏛️', color: '#8b5cf6', desc: 'Institution oversight, admissions approval, merit list publishing, payroll review', metrics: ['Admissions', 'Merit List', 'Payroll', 'Reports'] },
  { title: 'Dean', icon: '🎓', color: '#06b6d4', desc: 'Academic governance, faculty monitoring, departmental compliance and reporting', metrics: ['Departments', 'Faculty', 'Compliance', 'Stats'] },
  { title: 'Head of Department', icon: '👨‍🏫', color: '#10b981', desc: 'Dept management, class scheduling, student and faculty reassignments', metrics: ['Classes', 'Faculty', 'Students', 'Exams'] },
  { title: 'Faculty', icon: '📚', color: '#f59e0b', desc: 'Class management, manual attendance overrides, student evaluations, HOD tasks', metrics: ['Classes', 'Attendance', 'Evaluations', 'Leaves'] },
  { title: 'Student', icon: '🎒', color: '#ec4899', desc: 'Attendance tracking, exam schedules, thesis submissions, leave applications', metrics: ['Attendance', 'Exams', 'Thesis', 'Leaves'] },
  { title: 'Parent', icon: '👨‍👩‍👧', color: '#6366f1', desc: "Track child's attendance, fees, schedule, clinical rotations, announcements", metrics: ['Attendance', 'Fees', 'Schedule', 'Alerts'] },
  { title: 'Doctor', icon: '🩺', color: '#06b6d4', desc: 'OPD queue, clinical notes, vitals, structured prescriptions, ICD diagnoses', metrics: ['OPD Queue', 'Records', 'Prescriptions', 'Follow-ups'] },
  { title: 'Receptionist', icon: '🏥', color: '#10b981', desc: 'Patient registration, Aadhaar/ABHA capture, token generation, queue monitor', metrics: ['Patients', 'Tokens', 'Queue', 'Docs'] },
  { title: 'COE', icon: '📋', color: '#8b5cf6', desc: 'Exam timetables, evaluator bundles, answer sheets, marks publication', metrics: ['Timetables', 'Bundles', 'Marks', 'Ethics'] },
  { title: 'Lab Technician', icon: '🔬', color: '#f59e0b', desc: 'Sample collection, lab test processing, result entry, billing workflows', metrics: ['Samples', 'Tests', 'Results', 'Billing'] },
  { title: 'Finance Manager', icon: '💰', color: '#ec4899', desc: 'Fee collection, salary processing, expense tracking, financial reports', metrics: ['Fees', 'Payroll', 'Expenses', 'Reports'] },
  { title: 'Paper Evaluator', icon: '✍️', color: '#6366f1', desc: 'Secure answer script grading, red-pen annotation, video audit submissions', metrics: ['Bundles', 'Scripts', 'Marks', 'Videos'] },
  { title: 'Hostel Admin', icon: '🏠', color: '#10b981', desc: 'Room allocations, resident management, facility and inventory tracking', metrics: ['Rooms', 'Residents', 'Facilities', 'Reports'] },
  { title: 'Library Staff', icon: '📚', color: '#06b6d4', desc: 'Book catalog, issue and return management, digital resources', metrics: ['Catalog', 'Issued', 'Returns', 'Digital'] },
  { title: 'Transport Manager', icon: '🚌', color: '#8b5cf6', desc: 'Vehicle scheduling, route planning, student transport allocation', metrics: ['Routes', 'Vehicles', 'Students', 'Schedule'] },
  { title: 'Grievance Officer', icon: '⚖️', color: '#f59e0b', desc: 'Ticket triage, department escalation, resolution and closure management', metrics: ['Open', 'Assigned', 'Resolved', 'Stats'] },
];

const STATS = [
  { value: 18, suffix: '+', label: 'User Roles' },
  { value: 100, suffix: '+', label: 'ERP Modules' },
  { value: '∞', suffix: '', label: 'Biometric Events' },
  { value: 24, suffix: '/7', label: 'Cloud Access' },
  { value: 100, suffix: '%', label: 'Secure' },
  { value: 'AI', suffix: '', label: 'Smart Workflow' },
];

const WORKFLOWS = [
  { title: '🎓 Academic Journey', color: '#6366f1', steps: ['Application Submitted','Document Verification','Principal Approval','Merit List Generated','Student Enrolled','Biometric Registered','Attendance Tracked','Examinations','Graduation'] },
  { title: '🏥 Patient Journey', color: '#06b6d4', steps: ['Patient Walk-In','Registration / ABHA','OPD Token Issued','Doctor Consultation','Prescription Written','Lab Orders Placed','Results Ready','Follow-Up Scheduled'] },
  { title: '📋 Exam Pipeline', color: '#10b981', steps: ['Answer Sheets Uploaded','COE Creates Bundle','Evaluators Assigned','Digital Annotation','Video Audit Recorded','Marks Verified','Published to Students'] },
];



const HIGHLIGHTS = [
  { icon: '🔒', title: 'Enterprise Security', desc: 'JWT Auth, RBAC, encrypted storage, complete audit trails' },
  { icon: '⚡', title: 'Real-Time Processing', desc: 'Live biometric streams, instant queue updates, push events' },
  { icon: '☁️', title: 'Cloud Native', desc: 'Scalable, Docker-ready, multi-region deployable architecture' },
  { icon: '📱', title: 'Fully Responsive', desc: 'Optimized for mobile, tablet, desktop and 4K ultra-wide' },
  { icon: '🤖', title: 'AI Workflows', desc: 'Smart automation, pattern detection, predictive analytics' },
  { icon: '🏛️', title: 'Healthcare Compliant', desc: 'Standards-ready, privacy-first, audit-compliant platform' },
];

function AnimCounter({ value, suffix, active }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active || typeof value !== 'number') { setCount(value); return; }
    let cur = 0;
    const step = Math.ceil(value / 60);
    const t = setInterval(() => {
      cur = Math.min(cur + step, value);
      setCount(cur);
      if (cur >= value) clearInterval(t);
    }, 20);
    return () => clearInterval(t);
  }, [value, active]);
  return <>{typeof value === 'number' ? count : value}{suffix}</>;
}

const COURSE_LABELS = { MBBS:'MBBS', PG:'Post Graduate', SUPER_SPECIALITY:'Super Speciality', BDS:'BDS', BAMS:'BAMS' };

export default function LandingPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => localStorage.getItem('erp-theme') || 'light');
  const [roleIdx, setRoleIdx] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeWF, setActiveWF] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [meritList, setMeritList] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [meritLoading, setMeritLoading] = useState(true);
  const [meritSearch, setMeritSearch] = useState('');
  const [meritCourse, setMeritCourse] = useState('');
  const [showModal, setShowModal] = useState(false);
  const heroRef = useRef();
  const statsRef = useRef();
  const isDark = theme === 'dark';

  useEffect(() => {
    localStorage.setItem('erp-theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    return () => document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    fetch(`${API}/auth/admissions/merit-list/public/`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setIsPublished(data.published || false);
        setMeritList(Array.isArray(data.results) ? data.results : []);
      })
      .catch(() => {
        setIsPublished(false);
        setMeritList([]);
      })
      .finally(() => setMeritLoading(false));
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setRoleIdx(i => (i + 1) % ROLES.length), 3500);
    return () => clearInterval(t);
  }, []);

  const onMouseMove = useCallback(e => {
    if (!heroRef.current) return;
    const r = heroRef.current.getBoundingClientRect();
    setMousePos({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }, []);

  const scrollTo = id => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileMenu(false); };

  return (
    <div className={`lp ${isDark ? 'lp-dark' : 'lp-light'}`}>

      {/* ── NAVBAR ── */}
      <nav className={`lp-nav ${scrolled ? 'lp-nav-glass' : ''}`}>
        <div className="lp-nav-wrap">
          <button className="lp-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="lp-logo-icon">⚕</span>
            <span>Medical<span className="lp-accent">ERP</span></span>
          </button>
          <div className={`lp-links ${mobileMenu ? 'open' : ''}`}>
            {['home','features','roles','workflows','technology'].map(id => (
              <button key={id} onClick={() => scrollTo(id)} className="lp-link">
                {id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            ))}
          </div>
          <div className="lp-nav-right">
            <button className="lp-theme-toggle" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} aria-label="Toggle theme">
              {isDark ? '☀️' : '🌙'}
            </button>
            <button className="lp-ghost-btn" onClick={() => navigate('/login')}>Login</button>
            <button className="lp-solid-btn" onClick={() => navigate('/register')}>Sign Up</button>
            <button className="lp-hamburger" onClick={() => setMobileMenu(m => !m)}>
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero" id="home" ref={heroRef} onMouseMove={onMouseMove}>
        <div className="lp-aurora" />
        <div className="lp-hero-glow" style={{ left: `${mousePos.x * 100}%`, top: `${mousePos.y * 100}%` }} />
        <div className="lp-orbs">
          <div className="lp-orb o1" /><div className="lp-orb o2" /><div className="lp-orb o3" />
        </div>
        <div className="lp-hero-wrap">
          {/* Left */}
          <div className="lp-hero-left">
            <div className="lp-hero-pill">
              <span className="lp-pill-dot" />
              Next-Gen Enterprise Healthcare Platform
            </div>
            <h1 className="lp-h1">
              Medical<span className="lp-grad-text"> ERP</span>
              <span className="lp-h1-sub">Enterprise College &<br />Hospital Management</span>
            </h1>
            <p className="lp-hero-p">
              A unified digital ecosystem connecting Medical Education, Hospital Operations, Biometric Attendance, Admissions, OPD Management, Laboratory, Finance, and Institutional Governance.
            </p>
            <div className="lp-hero-actions">
              <button className="lp-btn-hero-ghost" onClick={() => scrollTo('features')}>▶ Explore</button>
            </div>
            <div className="lp-chips">
              {['18+ Roles','Biometric ADMS','OPD Queue','AI Workflow','RBAC Security','Cloud Native'].map(c => (
                <span key={c} className="lp-chip">{c}</span>
              ))}
            </div>
          </div>

          {/* Right — Image and floating widgets */}
          <div className="lp-hero-right">
            <div className="lp-hero-img-wrap">
              <img src={introImg} alt="Medical ERP" className="lp-hero-img" />
            </div>
          </div>

        </div>
      </section>

      {/* ── STATS ── */}
      <section className="lp-stats" ref={statsRef}>
        <div className="lp-stats-inner">
          {STATS.map((s,i)=>(
            <div key={i} className="lp-stat">
              <div className="lp-stat-n"><AnimCounter value={s.value} suffix={s.suffix} active={statsVisible}/></div>
              <div className="lp-stat-l">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES BENTO ── */}
      <section className="lp-section" id="features">
        <div className="lp-sec-head">
          <span className="lp-badge-label">FEATURES</span>
          <h2 className="lp-sec-h2">Everything Built <span className="lp-grad-text">In</span></h2>
          <p className="lp-sec-p">A complete enterprise ecosystem for modern medical institutions</p>
        </div>
        <div className="lp-bento">
          {FEATURES.map((f,i)=>(
            <div key={i} className="lp-fc" style={{'--ac':f.color}}>
              <span className="lp-fc-tag">{f.tag}</span>
              <span className="lp-fc-icon">{f.icon}</span>
              <h3 className="lp-fc-h3">{f.title}</h3>
              <p className="lp-fc-p">{f.desc}</p>
              <div className="lp-fc-shine"/>
            </div>
          ))}
        </div>
      </section>

      {/* ── ROLES ── */}
      <section className="lp-section lp-roles" id="roles">
        <div className="lp-sec-head">
          <span className="lp-badge-label">USER ROLES</span>
          <h2 className="lp-sec-h2">Designed for <span className="lp-grad-text">Every Role</span></h2>
          <p className="lp-sec-p">17+ specialized portals, each crafted for specific workflows</p>
        </div>
        <div className="lp-roles-grid">
          <div className="lp-role-card" style={{'--rc':ROLES[roleIdx].color}}>
            <div className="lp-rc-icon">{ROLES[roleIdx].icon}</div>
            <div className="lp-rc-title">{ROLES[roleIdx].title}</div>
            <div className="lp-rc-desc">{ROLES[roleIdx].desc}</div>
            <div className="lp-rc-metrics">
              {ROLES[roleIdx].metrics.map(m=><span key={m} className="lp-rc-metric">{m}</span>)}
            </div>
            <div className="lp-rc-counter">{roleIdx+1} / {ROLES.length}</div>
          </div>
          <div className="lp-role-pills">
            {ROLES.map((r,i)=>(
              <button key={i} onClick={()=>setRoleIdx(i)} className={`lp-rp ${i===roleIdx?'rp-active':''}`} style={i===roleIdx?{'--rc':r.color}:{}}>
                {r.icon} {r.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── WORKFLOWS ── */}
      <section className="lp-section" id="workflows">
        <div className="lp-sec-head">
          <span className="lp-badge-label">WORKFLOWS</span>
          <h2 className="lp-sec-h2">End-to-End <span className="lp-grad-text">Processes</span></h2>
          <p className="lp-sec-p">Intelligent workflows connecting every touchpoint</p>
        </div>
        <div className="lp-wf-tabs">
          {WORKFLOWS.map((w,i)=>(
            <button key={i} onClick={()=>setActiveWF(i)} className={`lp-wf-tab ${activeWF===i?'wf-active':''}`} style={activeWF===i?{'--wc':w.color}:{}}>
              {w.title}
            </button>
          ))}
        </div>
        <div className="lp-wf-flow">
          {WORKFLOWS[activeWF].steps.map((s,i)=>(
            <React.Fragment key={i}>
              <div className="lp-wf-step" style={{'--wc':WORKFLOWS[activeWF].color}}>
                <span className="lp-wf-num">{i+1}</span>
                <span className="lp-wf-label">{s}</span>
              </div>
              {i<WORKFLOWS[activeWF].steps.length-1&&<div className="lp-wf-arrow" style={{color:WORKFLOWS[activeWF].color}}>→</div>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── TECHNOLOGY ── */}
      <section className="lp-section lp-tech" id="technology">
        <div className="lp-sec-head">
          <span className="lp-badge-label">TECHNOLOGY</span>
          <h2 className="lp-sec-h2">World-Class <span className="lp-grad-text">Tech Stack</span></h2>
          <p className="lp-sec-p">Built on proven enterprise-grade technologies</p>
        </div>
      </section>

      {/* ── MERIT LIST ── */}
      <section className="lp-section lp-merit" id="merit-list">
        <div className="lp-sec-head">
          <span className="lp-badge-label">🏆 ADMISSIONS</span>
          <h2 className="lp-sec-h2">Published <span className="lp-grad-text">Merit List</span></h2>
          <p className="lp-sec-p">Approved candidates — check your name and login to complete enrollment</p>
        </div>
        {meritLoading ? (
          <div className="lp-merit-loading"><div className="lp-spinner"/><span>Loading merit list…</span></div>
        ) : !isPublished ? (
          <div className="lp-merit-empty">
            <span className="lp-merit-empty-icon">📋</span>
            <h3>Merit List Not Yet Published</h3>
            <p>The merit list will be published after admissions close. Check back soon or contact the Principal's office.</p>
            <button className="lp-btn-hero-outline" onClick={()=>navigate('/admission-form')}>Apply for Admission</button>
          </div>
        ) : meritList.length === 0 ? (
          <div className="lp-merit-empty">
            <span className="lp-merit-empty-icon">📋</span>
            <h3>No Candidates Found</h3>
            <p>No candidates have been approved for the merit list yet.</p>
          </div>
        ) : (
          <div className="lp-merit-banner-card">
            <div className="lp-merit-banner-icon">🏆</div>
            <h3>NEET Merit List is Live</h3>
            <p>The selection list of approved candidates for this academic year has been officially published.</p>
            <div className="lp-merit-banner-actions">
              <button className="lp-btn-hero-primary" onClick={() => setShowModal(true)}>
                👁️ View Merit List
              </button>
              <button className="lp-btn-hero-outline" onClick={() => navigate('/login')}>
                🔑 Login to Enroll
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── HIGHLIGHTS ── */}
      <section className="lp-section">
        <div className="lp-sec-head">
          <span className="lp-badge-label">ENTERPRISE</span>
          <h2 className="lp-sec-h2">Built for <span className="lp-grad-text">Scale</span></h2>
        </div>
        <div className="lp-hl-grid">
          {HIGHLIGHTS.map((h,i)=>(
            <div key={i} className="lp-hl-card">
              <div className="lp-hl-icon">{h.icon}</div>
              <h3 className="lp-hl-h3">{h.title}</h3>
              <p className="lp-hl-p">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta">
        <div className="lp-cta-orb1"/><div className="lp-cta-orb2"/>
        <div className="lp-cta-box">
          <span className="lp-badge-label">🏆 GET STARTED</span>
          <h2 className="lp-cta-h2">Transform Your Institution<br/><span className="lp-grad-text">Starting Today</span></h2>
          <p className="lp-cta-p">Experience the next generation of Medical College and Hospital Management — unified, intelligent, and always on.</p>
          <div className="lp-cta-btns">
            <button className="lp-btn-hero-primary" onClick={()=>navigate('/login')}>🔑 Login to Dashboard</button>
            <button className="lp-btn-hero-outline" onClick={()=>navigate('/register')}>✨ Create Account</button>
            <button className="lp-btn-hero-ghost" onClick={()=>navigate('/admission-form')}>📋 Apply for Admission</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-footer-wrap">
          <div className="lp-footer-brand">
            <div className="lp-logo"><span className="lp-logo-icon">⚕</span><span>Medical<span className="lp-accent">ERP</span></span></div>
            <p>Enterprise College & Hospital Management System</p>
          </div>
          <div className="lp-footer-links">
            <div><h4>Platform</h4><button onClick={()=>scrollTo('features')}>Features</button><button onClick={()=>scrollTo('roles')}>Roles</button><button onClick={()=>scrollTo('workflows')}>Workflows</button></div>
            <div><h4>Access</h4><button onClick={()=>navigate('/login')}>Login</button><button onClick={()=>navigate('/register')}>Register</button><button onClick={()=>navigate('/admission-form')}>Admission</button></div>
            <div><h4>Legal</h4><button>Privacy</button><button>Terms</button><button>Support</button></div>
          </div>
          <div className="lp-footer-right">
            <button className="lp-theme-toggle" onClick={()=>setTheme(t=>t==='dark'?'light':'dark')}>{isDark?'☀️ Light Mode':'🌙 Dark Mode'}</button>
          </div>
        </div>
        <div className="lp-footer-bar">© 2026 Medical ERP — Enterprise Healthcare Management Platform</div>
      </footer>

      {showModal && (
        <div className="lp-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="lp-modal-content" onClick={e => e.stopPropagation()}>
            <div className="lp-modal-header">
              <div>
                <span className="lp-modal-badge">🏆 ADMISSIONS</span>
                <h2>NEET Merit List</h2>
                <p>Approved candidates listed by NEET rank. Use the search bar to locate your application.</p>
              </div>
              <button className="lp-modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            
            <div className="lp-merit-controls">
              <input
                className="lp-merit-search"
                placeholder="🔍  Search by name or application number…"
                value={meritSearch}
                onChange={e=>setMeritSearch(e.target.value)}
              />
              <select className="lp-merit-filter" value={meritCourse} onChange={e=>setMeritCourse(e.target.value)}>
                <option value="">All Courses</option>
                {Object.entries(COURSE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </select>
              <button className="lp-btn-hero-primary" onClick={()=>{ setShowModal(false); navigate('/login'); }}>🔑 Login to Enroll</button>
            </div>

            {(() => {
              const filtered = meritList.filter(s => {
                const q = meritSearch.toLowerCase();
                const matchQ = !q || s.full_name?.toLowerCase().includes(q) || s.application_number?.toLowerCase().includes(q);
                const matchC = !meritCourse || s.course_applied === meritCourse;
                return matchQ && matchC;
              });
              
              if (filtered.length === 0) {
                return (
                  <div className="lp-merit-empty" style={{ margin: '40px 0' }}>
                    <span className="lp-merit-empty-icon">🔍</span>
                    <h3>No Matching Candidates</h3>
                    <p>No candidates found matching the query/filter. Please check your spelling or choose another course.</p>
                  </div>
                );
              }
              
              return (
                <div className="lp-merit-wrap">
                  <div className="lp-merit-summary">
                    <span>Showing <b>{filtered.length}</b> of <b>{meritList.length}</b> approved candidates</span>
                  </div>
                  <div className="lp-merit-table-wrap">
                    <table className="lp-merit-table">
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Application No.</th>
                          <th>Candidate Name</th>
                          <th>Course</th>
                          <th>Category</th>
                          <th>NEET Rank</th>
                          <th>NEET Score</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((s,i)=>(
                          <tr key={s.id||i} className="lp-merit-row">
                            <td><span className="lp-merit-rank">{i+1}</span></td>
                            <td><span className="lp-merit-appno">{s.application_number}</span></td>
                            <td><b className="lp-merit-name">{s.full_name}</b></td>
                            <td><span className="lp-merit-course">{COURSE_LABELS[s.course_applied]||s.course_applied}</span></td>
                            <td><span className="lp-merit-cat">{s.category}</span></td>
                            <td><span className="lp-merit-neet-rank">#{s.neet_rank}</span></td>
                            <td><span className="lp-merit-score">{s.neet_score}</span></td>
                            <td>
                              <button className="lp-merit-login-btn" onClick={()=>{ setShowModal(false); navigate('/login'); }}>
                                Login →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
